import React, { useState } from "react";
import {
  useInfiniteUnitsFiltered,
  useAllLocations,
} from "@api";
import { GenericDetailsModal, LocationDropdown, Table, ExportButton } from "@components";
import { useLocationOptions, useExport } from "@hooks";
import { UnitsTableProps, UnitStatus } from "./types";
import { statusOptions } from "./constants";
import {
  getUnitDetailsSections,
  useManageUnits,
  useGetUnits,
} from "./Unit";
import { STRING } from "@constants";

const T = STRING.UNIT_DETAILS;

export function Units({
  fixedStatus,
  hideStatusFilter = false,
  title,
}: UnitsTableProps) {
  const [locationId, setLocationId] = useState<string | null>(null);
  const [status, setStatus] = useState<UnitStatus | null>(fixedStatus ?? null);

  const { data, loading, hasMore, loadMore } = useInfiniteUnitsFiltered({
    locationId,
    status,
  });

  const { data: locations = [] } = useAllLocations();
  const locationOptions = useLocationOptions(locations);

  const {
    handleRowClick,
    selectedUnit,
    isDetailsModalOpen,
    handleCloseDetailsModal,
  } = useManageUnits();

  const { columns } = useGetUnits();

  const { exportData, isExporting } = useExport("/api/v1/units/export");

  const { sections } = getUnitDetailsSections(selectedUnit);

  return (
    <div className="overflow-hidden">
      <GenericDetailsModal
        visible={isDetailsModalOpen}
        onHide={handleCloseDetailsModal}
        sections={sections}
        header={T.MODAL_HEADER}
        noDataText={T.NO_DATA}
      />

      <Table
        columns={columns}
        data={data}
        title={title}
        onRowClicked={handleRowClick}
        loading={loading}
        loadMore={hasMore ? loadMore : undefined}
        extraHeader={
          <div className="mb-4 flex align-items-center gap-4 flex-wrap justify-content-between">
            <div className="flex align-items-center gap-4 flex-wrap">
              <div className="flex align-items-center gap-3">
                <span className="font-medium text-color">
                  Filter by Location:
                </span>
                <LocationDropdown
                  value={locationId}
                  options={locationOptions}
                  onChange={(e) => setLocationId(e.value)}
                  icon="pi-map-marker"
                />
              </div>

              {!hideStatusFilter && (
                <div className="flex align-items-center gap-3">
                  <span className="font-medium text-color">
                    Filter by Status:
                  </span>
                  <LocationDropdown
                    value={status}
                    options={statusOptions}
                    onChange={(e) => setStatus(e.value)}
                    icon="pi-tags"
                  />
                </div>
              )}
            </div>

            <ExportButton
              onExport={(format) =>
                exportData({
                  format,
                  locationId: locationId ?? undefined,
                  status: status ?? undefined,
                })
              }
              isExporting={isExporting}
            />
          </div>
        }
      />
    </div>
  );
}
